/**
 * Tracks how deep into our own app the current history entry is, so the back
 * button can tell whether one of our pages is behind it (safe to `router.back()`)
 * or only an external referrer (fall back to navigating to `/`). The depth is an
 * index stamped into each history entry, which survives layout remounts.
 */

export type NavDirection = 'forward' | 'back' | 'none';

export const IN_APP_NAVIGATION_STATE_KEY = '__inAppIdx';

let currentIdx = 0;
let initialized = false;
let pendingPop = false;
let lastDirection: NavDirection = 'none';

const readState = (): Record<string, unknown> | null => {
  try {
    return (window.history.state as Record<string, unknown> | null) ?? null;
  } catch {
    return null;
  }
};

const readIdx = (): number | null => {
  const value = readState()?.[IN_APP_NAVIGATION_STATE_KEY];
  return typeof value === 'number' ? value : null;
};

const writeIdx = (idx: number): void => {
  try {
    window.history.replaceState({ ...readState(), [IN_APP_NAVIGATION_STATE_KEY]: idx }, '');
  } catch {
    // ignore
  }
};

export const reconcileInAppNavigation = (): NavDirection => {
  const stamped = readIdx();

  if (!initialized) {
    initialized = true;
    pendingPop = false;
    currentIdx = stamped ?? 0;
    if (stamped == null) {
      writeIdx(currentIdx);
    }
    return 'none';
  }

  const isPop = pendingPop;
  pendingPop = false;

  let direction: NavDirection;
  if (stamped != null) {
    direction = stamped > currentIdx ? 'forward' : stamped < currentIdx ? 'back' : 'none';
    currentIdx = stamped;
  } else if (isPop) {
    direction = 'back';
    currentIdx = Math.max(0, currentIdx - 1);
    writeIdx(currentIdx);
  } else {
    direction = 'forward';
    currentIdx += 1;
    writeIdx(currentIdx);
  }

  if (direction !== 'none') {
    lastDirection = direction;
  }
  return direction;
};

export const notePopNavigation = (): void => {
  pendingPop = true;
};

export const getLastNavDirection = (): NavDirection => lastDirection;

export const canGoBackInApp = (): boolean => currentIdx > 0;
